import React, { ReactNode } from "react";
import { RecipientInfo } from "common/hooks/user/order/useGetOrderInfo";
import { enum_api_uri } from "common/hooks/utils/enum";
import { ViewLargeImg } from "user/components/common/Overlay";
import { formatDeliveryTimeFromDate } from "common/hooks/utils/utils";
import { ReactComponent as Arrow } from "../../../assets/icons/rightArrow.svg";

const Linkify = ({ text }: { text: string }) => {
    const linkifyText = (text: string) => {
        const urlPattern = /https?:\/\/[^\s]+/g; // URL 패턴
        const parts = text.split(urlPattern); // URL을 기준으로 문자열 분리
        const links = text.match(urlPattern); // URL 찾기

        return parts.map((part, index) => {
            // URL이 있는 경우 <a> 태그로 감싸기
            if (links && links[index]) {
                return (
                    <React.Fragment key={index}>
                        {part}
                        <a
                            href={links[index]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="break-words leading-[24px] text-[#3B82F6] underline"
                        >
                            {links[index]}
                        </a>
                    </React.Fragment>
                );
            }
            return part; // URL이 없는 경우 그냥 문자열 반환
        });
    };

    return <div className="break-all whitespace-pre-wrap">{linkifyText(text)}</div>;
};

export default function OrderDetailItems({
    values,
    orderDateFull,
    openOverlay,
    closeOverlay,
}: {
    values?: RecipientInfo;
    orderDateFull?: string;
    openOverlay: (content: ReactNode) => void;
    closeOverlay: () => void;
}) {
    // 즉시 배송 일때 -> '즉시배송' 즉시 배송 아닐 때 -> '오후 08시 40분 예식'
    // console.log(values?.delivery_time);
    const time = orderDateFull ?? "";
    // const deliveryTime = values?.delivery_immediate ? formatDeliveryTimeFromDate(time) : formatDeliveryTime(specificTime);
    const deliveryTime = values?.delivery_immediate ? formatDeliveryTimeFromDate(time) : values?.delivery_time;
    const isVisibleEvent = values?.event_content.depth1 || values?.event_content.depth2 || values?.related_person;

    return (
        <React.Fragment>
            <div className="wrapper border-t-[8px] border-[#f3f3f8]">
                <details open className="group">
                    <summary>
                        <div className="flex justify-between items-center py-[16px] border-b border-transparent group-open:border-[#ddd]">
                            <p className="font-bold">전할 메시지</p>
                            <Arrow className="group-open:rotate-[-90deg] rotate-[90deg]" />
                        </div>
                    </summary>
                    <ul className="flex flex-col gap-[4px] pt-[8px] pb-[16px]">
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">명의</span>
                            <span>{values?.wreath_sender ?? "-"}</span>
                        </li>
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">경조사어</span>
                            <span>{values?.wreath_word ?? "-"}</span>
                        </li>
                    </ul>
                </details>
            </div>

            <div className="wrapper border-t-[8px] border-[#f3f3f8]">
                <details open className="group">
                    <summary>
                        <div className="flex justify-between items-center py-[16px] border-b border-transparent group-open:border-[#ddd]">
                            <p className="font-bold">받으시는 분</p>
                            <Arrow className="group-open:rotate-[-90deg] rotate-[90deg]" />
                        </div>
                    </summary>
                    <ul className="flex flex-col gap-[4px] pt-[8px] pb-[16px]">
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">받으시는 분</span>
                            <span>
                                {values?.recipient_name} ({values?.recipient_mobile})
                            </span>
                        </li>
                        {isVisibleEvent && (
                            <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                                <span className="text-[#999]">경조내용</span>
                                <span>
                                    {values.event_content.event_content_idx === 2 ? (
                                        values.event_content.event_content
                                    ) : (
                                        <>
                                            {values?.event_content.depth1} {values?.event_content.depth2}{" "}
                                        </>
                                    )}

                                    {values?.related_person ? "/ " + values.related_person : ""}
                                </span>
                            </li>
                        )}

                        {values?.department && (
                            <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                                <span className="text-[#999]">부서</span>
                                <span>{values.department}</span>
                            </li>
                        )}
                        {values?.company_tel && (
                            <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                                <span className="text-[#999]">사번</span>
                                <span>{values?.company_tel}</span>
                            </li>
                        )}
                        {values?.work_location && (
                            <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                                <span className="text-[#999]">근무지</span>
                                <span>{values?.work_location}</span>
                            </li>
                        )}
                        {/* <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">노조가입 여부</span>
                            <span>{values?.is_union ? "가입" : "미가입"}</span>
                        </li> */}
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">배송일자</span>
                            <span>{values?.delivery_date}</span>
                        </li>
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">배송시간</span>
                            {/* <span>{values?.delivery_immediate ? `즉시배송 (${deliveryTime})` : deliveryTime}</span> */}
                            <span>{values?.delivery_immediate ? `즉시배송` : deliveryTime}</span>
                        </li>
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">배송주소</span>
                            <span className="break-keep">
                                ({values?.delivery_post}) {values?.delivery_address} {values?.delivery_address_detail}
                            </span>
                        </li>
                        <li className="grid grid-cols-[100px_1fr] leading-[32px]">
                            <span className="text-[#999]">배송메모</span>
                            <span>{values?.delivery_memo ?? "-"}</span>
                        </li>
                        <li className="flex flex-col leading-[32px]">
                            <span className="text-[#999]">초대장, 청첩장, 부고장 정보</span>
                            <ul className="flex gap-[4px] mt-[8px] mb-[4px]">
                                {values?.order_file.map(file => {
                                    return (
                                        <li
                                            key={file.file_idx}
                                            className="rounded-[8px] w-[60px] h-[60px] bg-[#eee] overflow-hidden"
                                            onClick={() =>
                                                openOverlay(
                                                    <ViewLargeImg
                                                        src={enum_api_uri.api_uri + "/" + file.file_name}
                                                        closeOverlay={closeOverlay}
                                                    />,
                                                )
                                            }
                                        >
                                            <img
                                                src={enum_api_uri.api_uri + "/" + file.file_name}
                                                alt={file.file_name}
                                                className="w-full h-full object-fit"
                                            />
                                        </li>
                                    );
                                })}
                            </ul>
                            <Linkify text={values?.file_memo ?? ""} />
                        </li>
                    </ul>
                </details>
            </div>
        </React.Fragment>
    );
}
